"use client";

import { lensConfigurationSteps } from "@/apiUtils/constants";
import { ConfiguratorProductInfo, LCSkuType, LensSummaryItemType } from "@/common/lib/types";
import { getLensConfigs } from "@/utils/lensConfigurator";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { lensConfigSteps, lensTypes, userSteps } from "../resource/config";

interface ILensConfiguratorState {
  sku: LCSkuType;
  loading: boolean;
  lensType: string;
  fromReviewStep: boolean;
  filters: Record<string, any>;
  excludePrescription?: boolean;
  summary: LensSummaryItemType[];
  steps: lensConfigurationSteps[];
  currentStep: lensConfigurationSteps;
  productInfo?: ConfiguratorProductInfo;
  skippedSteps: lensConfigurationSteps[];
  exitModalConfig: { enableMedia: boolean; media?: string };
}

interface ILensConfiguratorProviderProps extends ILensConfiguratorState {
  updateCurrentStep: (step: lensConfigurationSteps) => void;
  updateLensType: (lensType: string) => void;
  handleStepUpdate: (step: lensConfigurationSteps, filters: {}) => void;
  getNextStep: (
    step: lensConfigurationSteps,
    type: string,
    skip?: number,
  ) => lensConfigurationSteps;
  updateFilters: (filters: Record<string, any>) => void;
  setLoading: (isLoading: boolean) => void;
  updateSummary: (summary: LensSummaryItemType[]) => void;
  updateSkippedSteps: (skippedSteps: lensConfigurationSteps[]) => void;
  updateSku: (sku: LCSkuType) => void;
  updateExcludePrescription: (excludePrescription: boolean) => void;
  updateExitModalConfig: (enableMedia: boolean, media?: string) => void;
  updateFromReviewStep: (value: boolean | ((value: boolean) => boolean)) => void;
}

const LensConfiguratorContext = createContext({} as ILensConfiguratorProviderProps);

export const useConfiguratorContext = () => {
  const context = useContext(LensConfiguratorContext);
  if (!context)
    throw new Error(
      "To use useConfiguratorContext() component must be a children of LensConfiguratorProvider.",
    );
  return context;
};

const initialState: ILensConfiguratorState = {
  filters: {},
  summary: [],
  loading: false,
  fromReviewStep: false,
  excludePrescription: false,
  sku: { left: null, right: null },
  lensType: lensTypes.SINGLE_VISION,
  steps: lensConfigSteps[lensTypes.SINGLE_VISION],
  currentStep: lensConfigSteps[lensTypes.SINGLE_VISION][0],
  exitModalConfig: { enableMedia: false, media: undefined },
  skippedSteps: [
    lensConfigurationSteps.UPLOAD_PRESCRIPTION,
    lensConfigurationSteps.ADDITIONAL_OPTIONS,
    lensConfigurationSteps.ADD_TO_CART,
  ],
};

const LensConfiguratorProvider = ({
  children,
  initialStep,
}: {
  children: React.ReactNode;
  initialStep?: Partial<ILensConfiguratorState>;
}) => {
  const productInfo = initialStep?.productInfo;
  const prescriptionExclusion = [
    lensConfigurationSteps.PRESCRIPTION,
    lensConfigurationSteps.UPLOAD_PRESCRIPTION,
  ];

  const [state, setState] = useState<ILensConfiguratorState>({
    ...initialState,
    ...initialStep,
    steps: initialStep?.excludePrescription
      ? lensConfigSteps[initialStep?.lensType ?? lensTypes.SINGLE_VISION].filter(
          (step) => !prescriptionExclusion.includes(step),
        )
      : lensConfigSteps[initialStep?.lensType ?? lensTypes.SINGLE_VISION],
    summary: [
      {
        key: "model-price",
        option: "lens_config_model_label",
        choices: [
          { value: "", displayValue: { en: `${productInfo?.brand} ${productInfo?.name}` } },
        ],
        price: productInfo?.price?.salePrice ? `${productInfo.price.salePrice}` : "",
      },
      ...(initialStep?.summary ?? []),
    ],
  });

  const updateCurrentStep = useCallback((step: lensConfigurationSteps) => {
    setState((prevState) => {
      return {
        ...prevState,
        currentStep: step,
        filters: { ...prevState.filters, step },
      };
    });
  }, []);

  const getNextStep = useCallback(
    (step: lensConfigurationSteps, type: string, skip: number = 0) => {
      const steps = getLensConfigs(type, state.excludePrescription);
      const currentIndex = steps.indexOf(step);
      if (skip > 0) {
        const stepsInRange = steps.slice(currentIndex + 1, currentIndex + (1 + skip));
        setState((prevState) => ({
          ...prevState,
          skippedSteps: [...prevState.skippedSteps, ...stepsInRange],
        }));
      }
      return steps[currentIndex + (1 + skip)];
    },
    [state.excludePrescription],
  );

  const updateLensType = useCallback((lensType: string) => {
    setState((prevState) => {
      const steps = getLensConfigs(lensType, prevState.excludePrescription);
      return {
        ...prevState,
        lensType,
        steps,
        // filters: { lensType: lensType },
        skippedSteps:
          lensType === lensTypes.COMPUTER
            ? [lensConfigurationSteps.ADD_TO_CART]
            : [
                ...prevState.skippedSteps,
                lensConfigurationSteps.ADDITIONAL_OPTIONS,
                lensConfigurationSteps.UPLOAD_PRESCRIPTION,
              ],
      };
    });
  }, []);

  const handleStepUpdate = useCallback((nextStep: lensConfigurationSteps, filters: {}) => {
    setState((prevState) => ({
      ...prevState,
      filters: { ...prevState.filters, ...(filters || {}) },
      currentStep: nextStep,
    }));
  }, []);

  const setLoading = useCallback((isLoading: boolean) => {
    setState((prevState) => ({
      ...prevState,
      loading: isLoading,
    }));
  }, []);

  const updateSummary = useCallback((summary: LensSummaryItemType[]) => {
    setState((prevState) => ({
      ...prevState,
      summary: [...summary],
    }));
  }, []);

  const updateFilters = useCallback((filters: Record<string, any>) => {
    setState((prevState) => ({
      ...prevState,
      filters: { ...prevState.filters, ...filters },
    }));
  }, []);

  const updateSkippedSteps = useCallback((skippedSteps: lensConfigurationSteps[]) => {
    setState((prevState) => ({
      ...prevState,
      skippedSteps,
    }));
  }, []);

  const updateSku = useCallback((sku: LCSkuType) => {
    setState((prevState) => ({
      ...prevState,
      sku,
    }));
  }, []);

  const updateExcludePrescription = useCallback((excludePrescription: boolean) => {
    setState((prevState) => ({
      ...prevState,
      excludePrescription,
      steps: excludePrescription
        ? lensConfigSteps[prevState?.lensType ?? lensTypes.SINGLE_VISION].filter(
            (step) => !userSteps.includes(step),
          )
        : lensConfigSteps[prevState?.lensType ?? lensTypes.SINGLE_VISION],
    }));
  }, []);

  const updateExitModalConfig = useCallback((enableMedia: boolean, media?: string) => {
    setState((prevState) => ({ ...prevState, exitModalConfig: { enableMedia, media } }));
  }, []);

  const handleUpdateFromReview = useCallback((value: boolean | ((value: boolean) => boolean)) => {
    console.log("value", value);
    setState((state) => ({
      ...state,
      fromReviewStep: typeof value === "function" ? value(state.fromReviewStep) : value,
    }));
  }, []);

  const value = useMemo(() => {
    return {
      ...state,
      getNextStep,
      updateCurrentStep,
      updateLensType,
      handleStepUpdate,
      setLoading,
      updateSummary,
      updateFilters,
      updateSkippedSteps,
      updateSku,
      updateExcludePrescription,
      updateExitModalConfig,
      updateFromReviewStep: handleUpdateFromReview,
    };
  }, [
    state,
    getNextStep,
    updateCurrentStep,
    updateLensType,
    handleStepUpdate,
    setLoading,
    updateSummary,
    updateFilters,
    updateSkippedSteps,
    updateSku,
    updateExcludePrescription,
    updateExitModalConfig,
    handleUpdateFromReview,
  ]);

  return (
    <LensConfiguratorContext.Provider value={value}>{children}</LensConfiguratorContext.Provider>
  );
};

export default LensConfiguratorProvider;
